import React from 'react';
import { useFxRates } from './useFxRates';

/**
 * Unified FX hook built on top of useFxRates.
 *
 * - Loads FX rates for a given base currency (USD by default).
 * - Provides a helper to convert arbitrary currency amounts to the base.
 */
export function useFx(base: string = 'USD') {
  const fx = useFxRates(base);

  const convertToBase = React.useCallback(
    (amount: number | null | undefined, currency: string | null | undefined) => {
      const numericAmount = typeof amount === 'number' ? amount : Number(amount ?? 0);
      if (!Number.isFinite(numericAmount) || numericAmount === 0) {
        return { amountBase: 0, rate: null } as const;
      }

      const baseCurrency = fx.base?.toUpperCase() || base.toUpperCase();
      const fromCurrency = (currency || baseCurrency).toUpperCase();

      // Already in base currency
      if (fromCurrency === baseCurrency) {
        return { amountBase: numericAmount, rate: 1 } as const;
      }

      const rate = fx.getRateToBase(fromCurrency);
      if (rate === null || rate === 0) {
        // FX rate not available (not loaded yet, or unknown currency)
        return { amountBase: null, rate: null } as const;
      }

      // Rates are stored as: 1 BASE = rate * CURRENCY
      // To convert CURRENCY → BASE we divide: amount / rate
      const amountBase = numericAmount / rate;
      return { amountBase, rate } as const;
    },
    [fx, base]
  );

  return {
    ...fx,
    convertToBase,
  };
}
